import { ErrorCode } from '@trulyinbox/errors';
import { ApiError } from '@/lib/api';
import { getErrorMessage } from '@/lib/errors';
import { formatCurrency } from '@/lib/format';
import { formatDate, toDate } from '@/lib/date';
import type {
  ActiveDiscount,
  BillingPlan,
  ValidateCheckoutCodeResponse,
} from '@/lib/billing';
import { planCycle } from '@/features/billing/plan-helpers';
import type { LocalizedPrice } from '@/features/billing/use-price-preview';

/**
 * Non-component logic shared by every promo-code surface: the checkout dialog's
 * field (`CheckoutDialog`), the self-serve upgrade dialog's field
 * (`UpgradePromoField`), and the "already have a discount" gate both of those
 * and `RedeemCodeRow` render.
 *
 * The whole point of this module is that every rejection is branched on the
 * 6-digit `errorCode`, never on the server's message text — the data-layer rule
 * the bulk-operations client established.
 *
 * The two dialogs deliberately DON'T share a field component: checkout validates
 * a code against a plan it is about to buy (`POST /checkout/validate-code`,
 * returning `paddleDiscountId` to thread into Paddle.js), while the upgrade flow
 * validates by re-running the proration preview under a new query key. Same
 * copy, different data flow — so this module is the shared piece, not a
 * component with a props union covering both.
 */

/**
 * The collapsed `PromoCodeDisclosure` trigger. Declared once here rather than
 * inlined at each of the three call sites so the surfaces can't drift apart —
 * a customer meets this string on `/billing`, in the upgrade confirm dialog,
 * and in the checkout dialog, and it has to read identically in all three.
 * (Lives in this non-component module because `PromoCodeDisclosure.tsx` must
 * export only components to stay a Vite Fast Refresh boundary.)
 */
export const PROMO_DISCLOSURE_LABEL = 'Have a promo code?';

/**
 * User-facing copy for a rejected promo code.
 *
 * `NotApplicable` (041303) covers two distinct causes the backend cannot
 * distinguish for us without leaking catalog details: the code is bound to
 * another org, or it is restricted to plans that don't include this one. The
 * copy therefore names the PLAN case, because that is the one the customer can
 * act on (pick a different plan / cycle) — an org-bound code they don't own is
 * indistinguishable from an invalid one by design, and telling them "this belongs
 * to someone else" would confirm it exists.
 */
export function checkoutCodeErrorMessage(err: unknown, plan: BillingPlan | null): string {
  if (err instanceof ApiError) {
    switch (err.errorCode) {
      case ErrorCode.Billing.Discount.NotFound:
        return "We don't recognize that code. Check the spelling and try again.";
      case ErrorCode.Billing.Discount.Inactive:
        return 'That code has expired or is no longer active.';
      case ErrorCode.Billing.Discount.NotApplicable:
        return plan
          ? `That code isn't valid for the ${describePlanForCode(plan)} plan.`
          : "That code isn't valid for the plan you selected.";
      case ErrorCode.Billing.Discount.AlreadyRedeemed:
        return "You've already used that code.";
      case ErrorCode.Billing.Discount.DiscountAlreadyActive:
        // Paddle holds exactly one discount per subscription, so a second one
        // would clobber the first. Reachable even when `activeDiscount` said
        // there was none — that hint is null whenever Paddle is unreachable.
        return 'Your subscription already has a discount. You can apply a new code once it ends.';
      case ErrorCode.Billing.Discount.UsageLimitReached:
        return 'That code has reached its redemption limit.';
      case ErrorCode.Billing.Discount.PaddleSyncFailed:
        return "We couldn't verify that code right now. Please try again in a moment.";
    }
  }
  return getErrorMessage(err, "We couldn't apply that code.");
}

/** Every `Billing.Discount.*` code, for telling a REJECTED CODE apart from a
 * broken request. The upgrade flow needs this because both arrive as the same
 * failed preview query: a discount code means "this code is bad, fix the field
 * and keep the cached undiscounted quote", anything else (`041006 PreviewFailed`)
 * means "the quote itself failed, offer a retry". Derived from the catalog so a
 * future discount condition is covered without editing this list. */
const DISCOUNT_ERROR_CODES: readonly string[] = Object.values(ErrorCode.Billing.Discount);

export function isDiscountCodeError(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.errorCode != null &&
    DISCOUNT_ERROR_CODES.includes(err.errorCode)
  );
}

/**
 * Copy for the blocked promo field when a discount is already live —
 * "Your 20% discount runs until Sep 3, 2026 — you can apply a new code after
 * that." Returns `null` when there is nothing to say, so a call site can render
 * it with a plain truthiness check.
 *
 * Two degradations, both deliberate:
 *   - `endsAt: null` is a LIFETIME discount, not an unknown date. There is no
 *     "after that", so the copy points at support instead of promising a reopen.
 *   - the inner fields are all null for a discount created straight in the
 *     Paddle dashboard (no local catalog row), so we drop the amount and still
 *     name the date — the actionable half.
 */
export function describeActiveDiscount(discount: ActiveDiscount | null | undefined): string | null {
  if (!discount) return null;
  if (discount.endsAt == null) {
    return "You're on a permanent discount. Contact support to change it.";
  }
  const until = formatDate(toDate(discount.endsAt));
  const amount = describeDiscountAmount(discount);
  return amount
    ? `Your ${amount} discount runs until ${until}. You can apply a new code after that.`
    : `You have an active discount until ${until}.`;
}

/**
 * The short chip beside the plan price — "LAUNCH20 · 20% off", degrading to
 * whichever half is known, or "Discount applied" for a dashboard-created
 * discount with no local row. `describeActiveDiscount` carries the dates.
 */
export function activeDiscountChipLabel(discount: ActiveDiscount | null | undefined): string | null {
  if (!discount) return null;
  const amount = describeDiscountAmount(discount);
  const parts = [discount.code, amount ? `${amount} off` : null].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : 'Discount applied';
}

/** "20%" / "$15.00", or null when the discount has no local row to read it from. */
function describeDiscountAmount(discount: ActiveDiscount): string | null {
  if (discount.discountValue == null) return null;
  if (discount.discountType === 'percentage') return `${discount.discountValue}%`;
  if (discount.discountType === 'fixed') {
    return formatCurrency(discount.discountValue, discount.currency ?? 'USD');
  }
  return null;
}

/**
 * How to name a plan in the not-applicable message, e.g. `"Growth (yearly)"`.
 *
 * Spec §7.2 asks for copy like *"This code is only valid for yearly plans"*.
 * We can't state the code's actual restriction — it is a `restrictToPlanIds`
 * array the client never sees, and enumerating it would expose the catalog — so
 * we name what the customer DID pick instead. That is the actionable half: it
 * tells them the cycle they chose is the thing to change.
 */
export function describePlanForCode(plan: BillingPlan): string {
  return `${plan.name} (${planCycle(plan)})`;
}

/**
 * The three money figures the checkout dialog renders, all in ONE currency.
 *
 * Amounts are PER BILLING TERM (what the customer is about to be charged), not
 * per-month — the pricing card is the per-month surface. `currency` is
 * `undefined` only when neither Paddle nor the catalog named one, in which case
 * `formatCurrency`'s own `'USD'` default applies (so the fallback lives in one
 * place rather than being re-declared here).
 */
export type CheckoutSummary = {
  currency: string | undefined;
  /** List price before any discount; null when the plan carries no price. */
  listPrice: number | null;
  /** Savings for the term; null when no code is applied. */
  discount: number | null;
  /** Amount due today; null when no code is applied. */
  total: number | null;
};

/**
 * Resolve the checkout summary's money, preferring Paddle's geo-resolved
 * numbers over the flat USD catalog.
 *
 * The rule that matters: the three lines are **all-or-nothing per source**. A
 * localized list price beside a USD discount would produce a total that is
 * neither — so when a code is applied and Paddle's discounted re-preview is
 * unavailable, this falls all the way back to the catalog triple rather than
 * mixing. `POST /billing/checkout/validate-code` remains the AUTHORIZATION
 * check (is this code usable at all); `discounted` is the DISPLAY source.
 */
export function buildCheckoutSummary(params: {
  plan: BillingPlan;
  /** Paddle's undiscounted preview for this plan's checkout price id. */
  localizedPrice: LocalizedPrice | null;
  /** The validated code, or null when none is applied. */
  applied: ValidateCheckoutCodeResponse | null;
  /** Paddle's preview for the SAME price id WITH `discountId` set. */
  discounted: LocalizedPrice | null;
}): CheckoutSummary {
  const { plan, localizedPrice, applied, discounted } = params;
  const catalogPrice = plan.basePrice != null ? Number(plan.basePrice) : null;

  if (applied == null) {
    return {
      currency: localizedPrice?.currency ?? plan.currency,
      listPrice: localizedPrice?.amount ?? catalogPrice,
      discount: null,
      total: null,
    };
  }
  if (discounted != null) {
    // One Paddle response → list, discount and total agree by construction
    // (`subtotal − discount === total − tax`), in the visitor's own currency.
    return {
      currency: discounted.currency,
      listPrice: discounted.subtotal,
      discount: discounted.discount,
      total: discounted.amount,
    };
  }
  return {
    currency: applied.currency ?? plan.currency,
    listPrice: catalogPrice,
    discount: applied.savings,
    total: applied.total,
  };
}
